import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Configure axios defaults when token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      localStorage.setItem('token', token);
    } else {
      delete axios.defaults.headers.common['Authorization'];
      localStorage.removeItem('token');
    }
  }, [token]);

  // Check if user is authenticated on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (err) {
        console.error('Token verification failed:', err);
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    
    verifyToken();
  }, [token]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    setToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  }, []);

  const signup = useCallback(async (email, password, marketingOptIn = false, roleType = null) => {
    const response = await axios.post(`${API}/auth/signup`, { 
      email, 
      password,
      marketing_opt_in: marketingOptIn,
      role_type: roleType
    });
    setToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  }, []);

  const forgotPassword = useCallback(async (email) => {
    const response = await axios.post(`${API}/auth/forgot-password`, { email });
    return response.data;
  }, []);

  const resetPassword = useCallback(async (token, newPassword) => {
    const response = await axios.post(`${API}/auth/reset-password`, { 
      token, 
      new_password: newPassword 
    });
    return response.data;
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    const response = await axios.post(`${API}/auth/change-password`, { 
      current_password: currentPassword,
      new_password: newPassword 
    });
    return response.data;
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
  }, []);

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    login,
    signup,
    logout,
    forgotPassword,
    resetPassword,
    changePassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
